import omit from 'lodash-es/omit';
import uniq from 'lodash-es/uniq';
import values from 'lodash-es/values';
import moment from 'moment-timezone';
import { createAppSlice } from '.';
import {
  attachDeviceEquipment as attachDeviceEquipmentApi,
  deleteEquipmentHasDevice as deleteEquipmentHasDeviceApi,
  detachDeviceEquipment as detachDeviceEquipmentApi,
  editDevice as editDeviceApi,
  getDevice,
  getDeviceHistory,
  getDevices,
  linkTagToDevice as linkTagToDeviceApi,
  unlinkTagFromDevice as unlinkTagFromDeviceApi,
  updateEquipmentHasDevice as updateEquipmentHasDeviceApi,
} from '../api/api';
import { deviceInstance as deviceInstanceSchema, equipmentHasDevice as equipmentHasDeviceSchema } from '../api/schema';
import { DeviceStatus } from '../constants/deviceConstants';
import { normalize } from '../utils';
import { ItemWithPermissions } from '../utils/permissions';
import { KeysToCamelCase } from '../utils/types';
import { ClusterId } from './clusters';
import { EquipmentId } from './equipments';
import { alerting, success } from './snacks';
import { Tag, TagId } from './tags';

export type DeviceId = number;
export type EquipmentHasDeviceId = number;

type Device = ItemWithPermissions & {
  id: DeviceId;
  status: DeviceStatus;
  cluster_id: ClusterId;
  current_equipment_instance_id?: EquipmentId;
  location_latitude?: number;
  location_longitude?: number;
  location_date?: string;
  tags?: Tag[];
};
export type NormalizedDevice = KeysToCamelCase<Device>;

type DeviceModel = {
  id: number;
  equipment_type: string;
  type: string;
};
type NormalizedDeviceModel = KeysToCamelCase<DeviceModel>;

type EquipmentHasDevice = {
  id: EquipmentHasDeviceId;
  device_instance_id: DeviceId;
  equipment_instance_id: EquipmentId;
  device_instance?: DeviceId;
  equipment_instance?: EquipmentId;
  from_date: string;
  to_date: string | null;
};
type NormalizedEquipmentHasDevice = KeysToCamelCase<EquipmentHasDevice>;

type State = {
  isFetching: boolean;
  devicesIds: DeviceId[];
  deviceById: Record<DeviceId, NormalizedDevice>;
  deviceModelById: Record<string, NormalizedDeviceModel>;
  equipmentDevicesIds: EquipmentHasDeviceId[];
  equipmentDeviceById: Record<EquipmentHasDeviceId, NormalizedEquipmentHasDevice>;
};

const initialState: State = {
  isFetching: false,
  devicesIds: [],
  deviceById: {},
  deviceModelById: {},
  equipmentDevicesIds: [],
  equipmentDeviceById: {},
};

const devicesSlice = createAppSlice({
  name: 'devices',
  initialState,
  reducers: (create) => ({
    fetchDevices: create.asyncThunk(
      async (_, { dispatch }) => {
        const response = (await alerting(() => getDevices(), { dispatch })) as Device[];
        return normalize(response, [deviceInstanceSchema]) as {
          entities: {
            deviceInstance: Record<DeviceId, NormalizedDevice>;
            deviceModel: Record<string, NormalizedDeviceModel>;
          };
          result: DeviceId[];
        };
      },
      {
        pending: (state) => {
          state.isFetching = true;
        },
        fulfilled: (state, { payload }) => {
          const { deviceModel } = payload.entities;
          state.devicesIds = uniq([...state.devicesIds, ...payload.result]);
          state.deviceById = { ...state.deviceById, ...payload.entities.deviceInstance };
          values(deviceModel).forEach((model) => {
            state.deviceModelById[model.equipmentType] = omit(model, ['photo']) as NormalizedDeviceModel;
          });
        },
        settled: (state) => {
          state.isFetching = false;
        },
      },
    ),
    fetchDevice: create.asyncThunk(
      async (id: DeviceId, { dispatch }) => {
        const response = (await alerting(() => getDevice(id), { dispatch })) as Device;
        return normalize(response, deviceInstanceSchema) as {
          entities: { deviceInstance: Record<DeviceId, NormalizedDevice> };
          result: DeviceId;
        };
      },
      {
        fulfilled: (state, { payload }) => {
          state.deviceById = { ...state.deviceById, ...payload.entities.deviceInstance };
        },
      },
    ),
    fetchDeviceHistory: create.asyncThunk(
      async (id: DeviceId, { dispatch }) => {
        const response = (await alerting(() => getDeviceHistory(id), { dispatch })) as EquipmentHasDevice[];
        return normalize(response, [equipmentHasDeviceSchema]) as {
          entities: { equipmentHasDevice: Record<EquipmentHasDeviceId, NormalizedEquipmentHasDevice> };
          result: EquipmentHasDeviceId[];
        };
      },
      {
        fulfilled: (state, { payload }) => {
          const equipmentHasDeviceSuccess = payload.entities.equipmentHasDevice || {};
          const now = moment();
          Object.values(equipmentHasDeviceSuccess).forEach((ehd) => {
            const device = state.deviceById[ehd.deviceInstanceId];
            const isCurrent =
              moment(ehd.fromDate).isSameOrBefore(now) &&
              (ehd.toDate === undefined || ehd.toDate === null || moment(ehd.toDate).isAfter(now));
            if (device && isCurrent && device.currentEquipmentInstanceId !== ehd.equipmentInstanceId) {
              device.currentEquipmentInstanceId = ehd.equipmentInstanceId;
            }
          });
          state.equipmentDevicesIds = uniq([...payload.result, ...state.equipmentDevicesIds]);
          state.equipmentDeviceById = {
            ...state.equipmentDeviceById,
            ...equipmentHasDeviceSuccess,
          };
        },
      },
    ),
    editDevice: create.asyncThunk(
      async ({ id, payload }: { id: DeviceId; payload: Partial<NormalizedDevice> }, { dispatch }) => {
        const response = (await alerting(() => editDeviceApi(id, payload), { dispatch })) as Device;
        const normalized = normalize(response, deviceInstanceSchema) as {
          entities: { deviceInstance: Record<DeviceId, NormalizedDevice> };
          result: DeviceId;
        };
        success('Device.update_ok', { dispatch });
        return normalized;
      },
      {
        fulfilled: (state, { payload }) => {
          const device = payload.entities.deviceInstance[payload.result];
          const existing = state.deviceById[payload.result];
          if (existing) {
            Object.assign(existing, device);
          }
        },
      },
    ),
    attachDeviceEquipment: create.asyncThunk(
      async (
        { deviceId, payload }: { deviceId: DeviceId; payload: { equipment_id: EquipmentId; from_date: string } },
        { dispatch },
      ) => {
        const response = (await alerting(() => attachDeviceEquipmentApi(deviceId, payload), {
          dispatch,
        })) as EquipmentHasDevice[];
        const normalized = normalize(response, [equipmentHasDeviceSchema]) as {
          entities: { equipmentHasDevice: Record<EquipmentHasDeviceId, NormalizedEquipmentHasDevice> };
          result: EquipmentHasDeviceId[];
        };
        success('Beacon.update_success', { dispatch });
        return normalized;
      },
      {
        fulfilled: (state, { payload }) => {
          const { equipmentHasDevice } = payload.entities;
          const equipmentHasDeviceArray = Object.values(equipmentHasDevice);
          const now = moment();
          for (const key in state.deviceById) {
            const device = state.deviceById[Number(key)]!;
            let { currentEquipmentInstanceId } = device;
            for (const ehd of equipmentHasDeviceArray) {
              const isCurrent =
                moment(ehd.fromDate).isSameOrBefore(now) &&
                (ehd.toDate === undefined || ehd.toDate === null || moment(ehd.toDate).isAfter(now));
              if (isCurrent) {
                if (ehd.equipmentInstanceId === device.currentEquipmentInstanceId) {
                  currentEquipmentInstanceId = 0;
                  break;
                }
                if (ehd.deviceInstanceId === device.id) {
                  currentEquipmentInstanceId = ehd.equipmentInstanceId;
                  break;
                }
              }
              if (ehd.deviceInstanceId === device.id) {
                currentEquipmentInstanceId = 0;
              }
            }
            device.currentEquipmentInstanceId = currentEquipmentInstanceId;
          }
          state.equipmentDevicesIds = payload.result;
          state.equipmentDeviceById = equipmentHasDevice;
        },
      },
    ),
    detachDeviceEquipment: create.asyncThunk(
      async (deviceId: DeviceId, { dispatch }) => {
        const response = (await alerting(() => detachDeviceEquipmentApi(deviceId), {
          dispatch,
        })) as EquipmentHasDevice[];
        const normalized = normalize(response, [equipmentHasDeviceSchema]) as {
          entities: { equipmentHasDevice: Record<EquipmentHasDeviceId, NormalizedEquipmentHasDevice> };
          result: EquipmentHasDeviceId[];
        };
        success('Beacon.update_success', { dispatch });
        return normalized;
      },
      {
        fulfilled: (state, { payload }) => {
          const { equipmentHasDevice } = payload.entities;
          const equipmentHasDeviceArray = Object.values(equipmentHasDevice);
          const now = moment();
          for (const key in state.deviceById) {
            const device = state.deviceById[Number(key)]!;
            let { currentEquipmentInstanceId } = device;
            for (const ehd of equipmentHasDeviceArray) {
              const isCurrent =
                moment(ehd.fromDate).isSameOrBefore(now) &&
                (ehd.toDate === undefined || ehd.toDate === null || moment(ehd.toDate).isAfter(now));
              if (isCurrent) {
                if (ehd.equipmentInstanceId === device.currentEquipmentInstanceId) {
                  currentEquipmentInstanceId = 0;
                  break;
                }
                if (ehd.deviceInstanceId === device.id) {
                  currentEquipmentInstanceId = ehd.equipmentInstanceId;
                  break;
                }
              }
              if (ehd.deviceInstanceId === device.id) {
                currentEquipmentInstanceId = 0;
              }
            }
            device.currentEquipmentInstanceId = currentEquipmentInstanceId;
          }
          state.equipmentDevicesIds = payload.result;
          state.equipmentDeviceById = equipmentHasDevice;
        },
      },
    ),
    deleteEquipmentHasDevice: create.asyncThunk(
      async (id: EquipmentHasDeviceId, { dispatch, getState }) => {
        const entry = (getState() as { devices: State }).devices.equipmentDeviceById[id];
        await alerting(() => deleteEquipmentHasDeviceApi(id), { dispatch });
        success('EquipmentHasDevice.delete_success', { dispatch });
        return { id, entry };
      },
      {
        fulfilled: (state, { payload: { id, entry: ehd } }) => {
          state.equipmentDevicesIds = state.equipmentDevicesIds.filter((eid) => eid !== id);
          delete state.equipmentDeviceById[id];

          if (ehd) {
            const { fromDate, toDate, deviceInstanceId } = ehd;
            const now = moment();
            const isCurrent =
              moment(fromDate).isSameOrBefore(now) &&
              (toDate === undefined || toDate === null || moment(toDate).isAfter(now));
            if (isCurrent) {
              const device = state.deviceById[deviceInstanceId];
              if (device) {
                device.currentEquipmentInstanceId = undefined;
              }
            }
          }
        },
      },
    ),
    updateEquipmentHasDevice: create.asyncThunk(
      async (
        {
          id,
          payload,
          deviceId,
        }: {
          id: EquipmentHasDeviceId;
          payload: Partial<NormalizedEquipmentHasDevice>;
          deviceId: DeviceId;
        },
        { dispatch },
      ) => {
        await alerting(() => updateEquipmentHasDeviceApi(id, payload), { dispatch });
        success('EquipmentHasDevice.update_success', { dispatch });
        await dispatch(fetchDeviceHistory(deviceId));
      },
    ),
    linkTagToDevice: create.asyncThunk(
      async ({ deviceId, tag }: { deviceId: DeviceId; tag: Tag }, { dispatch }) => {
        await alerting(() => linkTagToDeviceApi(deviceId, tag.id), { dispatch });
      },
      {
        fulfilled: (
          state,
          {
            meta: {
              arg: { deviceId, tag },
            },
          },
        ) => {
          const device = state.deviceById[deviceId];
          if (device) {
            if (!device.tags) {
              device.tags = [];
            }
            device.tags.push(tag);
          }
        },
      },
    ),
    unlinkTagFromDevice: create.asyncThunk(
      async ({ deviceId, tagId }: { deviceId: DeviceId; tagId: TagId }, { dispatch }) => {
        await alerting(() => unlinkTagFromDeviceApi(deviceId, tagId), { dispatch });
      },
      {
        fulfilled: (
          state,
          {
            meta: {
              arg: { deviceId, tagId },
            },
          },
        ) => {
          const device = state.deviceById[deviceId];
          if (device && device.tags) {
            device.tags = device.tags.filter((tag) => tag.id !== tagId);
          }
        },
      },
    ),
    devicePositionUpdated: create.reducer(
      (
        state,
        {
          payload,
        }: {
          payload: {
            deviceId: DeviceId;
            locationLatitude: number;
            locationLongitude: number;
            locationDate: string;
          };
        },
      ) => {
        const device = state.deviceById[payload.deviceId];
        if (device) {
          device.locationLatitude = payload.locationLatitude;
          device.locationLongitude = payload.locationLongitude;
          device.locationDate = payload.locationDate;
        }
      },
    ),
  }),
  selectors: {
    selectDevicesIds: (state) => state.devicesIds,
    selectDevicesById: (state) => state.deviceById,
    selectDeviceModelById: (state) => state.deviceModelById,
    selectAreDevicesFetching: (state) => state.isFetching,
    selectEquipmentDevicesIds: (state) => state.equipmentDevicesIds,
    selectEquipmentDeviceById: (state) => state.equipmentDeviceById,
  },
});

export const {
  selectAreDevicesFetching,
  selectDeviceModelById,
  selectDevicesById,
  selectDevicesIds,
  selectEquipmentDeviceById,
} = devicesSlice.selectors;

export const {
  fetchDevices,
  fetchDevice,
  fetchDeviceHistory,
  editDevice,
  attachDeviceEquipment,
  detachDeviceEquipment,
  deleteEquipmentHasDevice,
  updateEquipmentHasDevice,
  linkTagToDevice,
  unlinkTagFromDevice,
  devicePositionUpdated,
} = devicesSlice.actions;

export default devicesSlice.reducer;
